import { level14 } from "@pindou/game-content";
import {
  advanceTime,
  createGame,
  expandTray,
  getConnectedBlock,
  placeSelectedBlockInTray,
  selectBlock,
} from "@pindou/game-core";
import type { GameState, Point, TrayBead } from "@pindou/game-core";
import { BoardRenderer } from "../render/board-renderer";
import { beadCss } from "../render/colors";
import { playEffects } from "../render/effect-player";
import { icon } from "./icons";

export interface GameScreenOptions {
  readonly trayFrameUrl: string;
  readonly useIntro: boolean;
  readonly onHome: () => void;
}

export interface GameScreenHandle {
  readonly destroy: () => void;
}

declare global {
  interface Window {
    __GAME_TEST__?: {
      readonly snapshot: () => GameState;
      readonly reset: () => void;
      readonly metrics: () => { readonly trayCount: number; readonly litTargets: number };
    };
  }
}

function formatTime(milliseconds: number): string {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function required<T extends Element>(host: ParentNode, selector: string): T {
  const element = host.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing element: ${selector}`);
  }
  return element;
}

export function createGameScreen(
  host: HTMLElement,
  options: GameScreenOptions,
): GameScreenHandle {
  host.innerHTML = `
    <section class="screen game-screen ${options.useIntro ? "is-intro" : ""}" data-screen="game">
      <header class="game-hud">
        <button class="plain-icon-button game-settings" type="button" aria-label="暂停与设置">${icon("gear")}</button>
        <div class="level-heading"><strong>${level14.title}</strong><div class="timer-pill">${icon("clock")}<span>${formatTime(level14.durationMs)}</span></div></div>
        <div class="hud-balance"><span>目标</span><strong>0/8</strong></div>
      </header>

      <main class="play-zone">
        <div class="board-viewport">
          <canvas class="board-canvas" aria-label="拼豆棋盘"></canvas>
          <div class="zoom-control">
            <button type="button" data-zoom="up" aria-label="放大">+</button>
            <input type="range" min="82" max="116" value="100" aria-label="棋盘缩放" />
            <button type="button" data-zoom="down" aria-label="缩小">−</button>
          </div>
        </div>

        <section class="tray-panel" aria-label="拼豆托盘">
          <img src="${options.trayFrameUrl}" alt="" aria-hidden="true" />
          <div class="tray-grid"></div>
          <button class="expand-tray" type="button">增加2行</button>
        </section>

        <nav class="tool-row" aria-label="关卡道具">
          <button type="button" data-tool="磁铁">${icon("magnet")}<span>磁铁</span><i>+</i></button>
          <button type="button" data-tool="清理">${icon("brush")}<span>清理</span><i>1</i></button>
          <button type="button" data-tool="沙漏">${icon("hourglass")}<span>沙漏</span><i>+</i></button>
        </nav>
      </main>

      <div class="tutorial-callout"><strong>每一步都由你决定</strong><span>先选整块拼豆，再点一个空白备用格</span></div>
      <div class="flight-layer" aria-hidden="true"></div>
      <div class="toast" role="status" aria-live="polite"></div>
      <div class="modal-layer" role="dialog" aria-modal="true"></div>
    </section>
  `;

  const screen = required<HTMLElement>(host, ".game-screen");
  const canvas = required<HTMLCanvasElement>(screen, ".board-canvas");
  const renderer = new BoardRenderer(canvas, level14);
  const trayGrid = required<HTMLElement>(screen, ".tray-grid");
  const trayPanel = required<HTMLElement>(screen, ".tray-panel");
  const expandButton = required<HTMLButtonElement>(screen, ".expand-tray");
  const flightLayer = required<HTMLElement>(screen, ".flight-layer");
  const timerText = required<HTMLElement>(screen, ".timer-pill span");
  const tutorial = required<HTMLElement>(screen, ".tutorial-callout");
  const modal = required<HTMLElement>(screen, ".modal-layer");
  let state = createGame(level14);
  let displayTray: readonly TrayBead[] = [];
  let highlighted = new Set<string>(), departingBeadIds = new Set<string>();
  let paused = false;
  let animating = false;
  let destroyed = false;
  let lastTick = performance.now();
  let toastTimer = 0;

  const viewState = (): GameState => ({
    ...state,
    removedBeadIds: departingBeadIds.size > 0
      ? [...new Set([...state.removedBeadIds, ...departingBeadIds])]
      : state.removedBeadIds,
    tray: displayTray,
  });
  const draw = (): void => {
    renderer.draw(viewState(), highlighted);
    timerText.textContent = formatTime(state.remainingMs);
  };

  const renderTray = (): void => {
    const rows = Math.ceil(state.trayCapacity / 12);
    trayGrid.style.setProperty("--tray-rows", String(rows));
    trayPanel.classList.toggle("is-expanded", rows > 3);
    trayPanel.classList.toggle("is-full", displayTray.length >= state.trayCapacity);
    expandButton.disabled = state.trayCapacity >= state.rules.maxTrayCapacity;
    expandButton.textContent = expandButton.disabled ? "已增加2行" : "增加2行";
    trayGrid.innerHTML = Array.from({ length: state.trayCapacity }, (_, index) => {
      const bead = displayTray.find((candidate) => candidate.trayIndex === index);
      return `<div class="tray-slot ${bead ? "is-occupied" : "is-empty"}" data-index="${index}">${bead ? `<b class="tray-bead" style="${beadCss(bead.color)}"></b>` : ""}</div>`;
    }).join("");
  };

  const setTray = (tray: readonly TrayBead[]): void => {
    displayTray = [...tray].sort((left, right) => left.trayIndex - right.trayIndex);
    renderTray();
  };

  const trayCenter = (index: number): DOMPoint => {
    const slots = trayGrid.querySelectorAll<HTMLElement>(".tray-slot");
    const slot = slots[Math.min(index, slots.length - 1)];
    const rect = slot?.getBoundingClientRect() ?? trayGrid.getBoundingClientRect();
    return new DOMPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  const showToast = (message: string): void => {
    const toast = required<HTMLElement>(screen, ".toast");
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add("is-visible");
    toastTimer = window.setTimeout(() => toast.classList.remove("is-visible"), 2100);
  };

  const showResult = (): void => {
    paused = true;
    const won = state.status === "won";
    modal.innerHTML = `<div class="result-card ${won ? "won" : "failed"}">
      <div class="result-mark">${won ? "完成" : "未完成"}</div>
      <h2>${won ? "拼豆图已归位" : "本关时间到"}</h2>
      <p>${won ? "8种颜色全部完成，托盘也已清空。" : "调整收集顺序，再试一次。"}</p>
      <button type="button" data-result="restart">${icon("restart")}重新挑战</button>
      <button type="button" data-result="home">${icon("home")}返回主页</button>
    </div>`;
    modal.classList.add("is-visible");
  };

  const reset = (): void => {
    state = createGame(level14);
    displayTray = [];
    highlighted.clear();
    departingBeadIds.clear();
    paused = false;
    animating = false;
    lastTick = performance.now();
    modal.classList.remove("is-visible");
    tutorial.classList.remove("is-hidden");
    renderTray();
    draw();
  };

  const handleSelect = (point: Point): void => {
    if (paused || animating || state.status !== "playing") {
      return;
    }
    const block = getConnectedBlock(level14.board, state, point);
    if (block.length === 0) {
      showToast("这里已经没有可收集的拼豆");
      return;
    }
    const update = selectBlock(level14, state, point);
    if (!update.accepted) {
      return;
    }
    state = update.state;
    highlighted = new Set(state.selectedBeadIds);
    draw();
    showToast(`已选中${block.length}颗拼豆，请点击空白备用格`);
  };

  const handleTrayPlace = async (startIndex: number): Promise<void> => {
    if (paused || animating || state.status !== "playing") return;
    const selectedIds = [...state.selectedBeadIds];
    const update = placeSelectedBlockInTray(level14, state, startIndex);
    if (!update.accepted) {
      if (update.reason === "nothing-selected") showToast("先在棋盘上选中一整块拼豆");
      if (update.reason === "tray-space-unavailable") showToast("从这里开始没有足够连续的空白备用格");
      return;
    }
    animating = true;
    await wait(80);
    departingBeadIds = new Set(selectedIds);
    highlighted.clear();
    draw();
    await playEffects(update.effects, {
      layer: flightLayer,
      boardCenter: (source) => renderer.cellCenterClient(source),
      trayCenter,
      getTray: () => displayTray,
      setTray,
    });
    state = update.state;
    departingBeadIds.clear();
    displayTray = [...state.tray];
    renderTray();
    draw();
    tutorial.classList.add("is-hidden");
    animating = false;
    if (state.status !== "playing") {
      showResult();
    }
  };

  canvas.addEventListener("pointermove", (event) => {
    if (animating || paused) {
      return;
    }
    const point = renderer.pointFromPointer(event);
    highlighted = new Set(point ? getConnectedBlock(level14.board, state, point).map((bead) => bead.beadId) : []);
    draw();
  });
  canvas.addEventListener("pointerleave", () => { highlighted.clear(); draw(); });
  canvas.addEventListener("pointerup", (event) => {
    const point = renderer.pointFromPointer(event);
    if (point) {
      handleSelect(point);
    }
  });
  trayGrid.addEventListener("pointerup", (event) => {
    const slot = (event.target as HTMLElement).closest<HTMLElement>(".tray-slot");
    const index = Number(slot?.dataset.index);
    if (Number.isInteger(index)) void handleTrayPlace(index);
  });

  const openSettings = (): void => {
    paused = true;
    modal.innerHTML = `<div class="settings-card"><h2>设置</h2><p>优先点击大片拼豆的边缘，可有效减少拼豆落单</p>
      <div class="setting-toggles"><button type="button" data-toggle>音效<span>关</span></button><button type="button" data-toggle>音乐<span>关</span></button><button type="button" data-toggle>震动<span>关</span></button></div>
      <button class="primary-modal-button" type="button" data-settings="resume">继续游戏</button>
      <button type="button" data-settings="restart">${icon("restart")}重新挑战</button>
      <button type="button" data-settings="home">${icon("home")}返回主页</button></div>`;
    modal.classList.add("is-visible");
  };

  screen.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.closest(".game-settings")) openSettings();
    const settings = target.closest<HTMLElement>("[data-settings]")?.dataset.settings;
    const result = target.closest<HTMLElement>("[data-result]")?.dataset.result;
    if (settings === "resume") { modal.classList.remove("is-visible"); paused = false; lastTick = performance.now(); }
    if (settings === "restart" || result === "restart") reset();
    if (settings === "home" || result === "home") options.onHome();
    if (target.closest(".expand-tray")) {
      const update = expandTray(level14, state);
      if (update.accepted) {
        state = update.state;
        renderTray();
        showToast("托盘已增加2行，可以继续收集");
      }
    }
    const toggle = target.closest<HTMLButtonElement>("[data-toggle]");
    if (toggle) { toggle.classList.toggle("is-on"); required<HTMLElement>(toggle, "span").textContent = toggle.classList.contains("is-on") ? "开" : "关"; }
    const tool = target.closest<HTMLElement>("[data-tool]")?.dataset.tool;
    if (tool) showToast(`${tool}的具体效果未在录屏出现，本试玩不臆造规则`);
  });

  const zoom = required<HTMLInputElement>(screen, ".zoom-control input");
  const applyZoom = (): void => screen.style.setProperty("--board-zoom", String(Number(zoom.value) / 100));
  zoom.addEventListener("input", applyZoom);
  screen.querySelectorAll<HTMLButtonElement>("[data-zoom]").forEach((button) => button.addEventListener("click", () => {
    zoom.value = String(Math.min(116, Math.max(82, Number(zoom.value) + (button.dataset.zoom === "up" ? 6 : -6))));
    applyZoom();
  }));

  const tick = window.setInterval(() => {
    const now = performance.now();
    const elapsed = now - lastTick;
    lastTick = now;
    if (paused || animating || state.status !== "playing") return;
    const update = advanceTime(level14, state, elapsed);
    if (update.accepted) state = update.state;
    draw();
    if (state.status !== "playing") showResult();
  }, 250);
  const resize = new ResizeObserver(draw);
  resize.observe(canvas);
  renderTray();
  draw();
  applyZoom();
  if (options.useIntro) window.setTimeout(() => screen.classList.remove("is-intro"), 1450);
  window.__GAME_TEST__ = { snapshot: () => structuredClone(state), reset, metrics: () => ({ trayCount: state.tray.length, litTargets: state.litTargetSlotIds.length }) };

  return {
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      window.clearInterval(tick);
      window.clearTimeout(toastTimer);
      resize.disconnect();
      delete window.__GAME_TEST__;
    },
  };
}
